import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle, Clock } from "lucide-react";
import { Seo } from "@/components/site/Section";
import { http } from "@/lib/api";

const MAX_POLLS = 20;

const VIEWS = {
  pending: { icon: Loader2, spin: true, title: "Confirming your payment…", text: "This usually takes a few seconds. Please don't close this page." },
  slow: { icon: Clock, title: "Still confirming with our payment provider", text: "Your payment is being checked. We'll email you as soon as it's confirmed. If you were charged, your subscription will be set up automatically." },
  paid: { icon: CheckCircle2, title: "Welcome to WeeLogRun", text: "Your payment is confirmed and your subscription is set up. We'll email you to confirm your delivery day." },
  failed: { icon: XCircle, title: "Payment not completed", text: "Your payment didn't go through and you haven't been charged. You can try again." },
  notfound: { icon: XCircle, title: "We couldn't find that checkout", text: "If you think you've been charged, please contact us." },
};

export default function CheckoutReturn() {
  const [params] = useSearchParams();
  const ref = params.get("session_id");
  const [view, setView] = useState("pending");
  const [info, setInfo] = useState(null);

  useEffect(() => {
    let n = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await http.get(`/checkout/status/${ref}`);
        setInfo(data);
        if (data.status === "paid") {
          sessionStorage.removeItem("wlr_checkout_draft");
          return setView("paid");
        }
        if (data.status === "failed" || data.status === "error") return setView("failed");
      } catch (err) {
        if (err?.response?.status === 404) return setView("notfound");
      }
      n += 1;
      if (n >= MAX_POLLS) return setView("slow");
      timer = setTimeout(poll, 3000);
    };
    if (ref) poll();
    else setView("notfound");
    return () => clearTimeout(timer);
  }, [ref]);

  const v = VIEWS[view];
  const Icon = v.icon;
  return (
    <div className="lc-container grid min-h-[80vh] place-items-center pt-28 pb-20" data-testid="checkout-return-page">
      <Seo title="Payment status" noindex />
      <div className="max-w-lg rounded-3xl border border-[#262626] bg-[#101010] p-8 text-center sm:p-10" role="status" aria-live="polite">
        <Icon className={`mx-auto h-12 w-12 text-[#FF6A00] ${v.spin ? "animate-spin" : ""}`} />
        <h1 className="mt-6 font-display text-3xl font-bold" data-testid="checkout-return-title">
          {view === "paid" && info?.first_name ? `Welcome, ${info.first_name}` : v.title}
        </h1>
        <p className="mt-3 text-[#A1A1AA]" data-testid="checkout-return-text">
          {view === "paid" && info ? `Your ${info.plan_name}${/plan|package/i.test(info.plan_name) ? "" : " plan"} is confirmed. ` : ""}{v.text}
        </p>
        <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
          {view === "failed" && <Link to="/plans" className="lc-btn-primary" data-testid="checkout-return-retry">Try again</Link>}
          {view === "paid" && <Link to="/account" className="lc-btn-primary" data-testid="checkout-return-account">Go to My WeeLogRun</Link>}
          <Link to={view === "notfound" ? "/contact" : "/"} className="lc-btn-ghost" data-testid="checkout-return-home">{view === "notfound" ? "Contact us" : "Back to home"}</Link>
        </div>
      </div>
    </div>
  );
}
